import {RemoveAccountUserCommand} from '@perfectpost/perfect-post-common';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {signOut} from 'aws-amplify/auth';

import {sendOrThrow} from '../../../services/sendOrThrow';
import {safeClear} from '../../../utils/safeLocalforage';
import type {MutationUiOptions} from '../../../utils/mutationMeta';

/**
 * Statut HTTP d'un refus de suppression de compte.
 *
 * 409 = l'utilisateur est seul propriétaire d'une équipe avec un abonnement en
 * cours. Le thunk le propageait par `rejectWithValue({statusCode})` avec ce
 * commentaire : « createAsyncThunk sérialise sinon l'erreur et le perd ». Une
 * mutation propage l'erreur telle quelle ; cette fonction la lit.
 */
export const getRemoveAccountStatusCode = (error: unknown): number | undefined =>
  (error as {response?: {statusCode?: number}} | undefined)?.response?.statusCode;

/**
 * Supprime définitivement le compte.
 *
 * Remplace le thunk `removeAccount` de `src/store/mainslice.ts`.
 */
export function useRemoveAccountMutation(options: MutationUiOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async () => {
      await sendOrThrow(new RemoveAccountUserCommand());
      return signOut();
    },
    onSuccess: async () => {
      localStorage.clear();
      // Ne rejette jamais : le cache React Query doit être vidé quoi qu'il arrive.
      await safeClear();
      queryClient.clear();
    },
  });
}
